import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { X, Package, ChevronDown, ChevronRight, CheckCircle2, AlertCircle, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * VendorQuoteImportModal
 *
 * Lets the user — while drafting a Customer Quotation — browse the
 * Vendor Quotations (supplier-side price intel) and copy specific
 * lines into the customer quote's items array.
 *
 * Server contract:
 *   GET /api/vendor-quotations?project_id=<id>
 *     → { quotations: [ { id, quote_no, supplier_name, lines: [...] } ] }
 *
 * Selected vendor lines are returned to the parent via onImport(items)
 * shaped to match the Customer Quotation item schema:
 *   { description, quantity, unit, unit_price, total }
 */
const VendorQuoteImportModal = ({ projectId, projectName, onClose, onImport }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [quotations, setQuotations] = useState([]);
  const [expanded, setExpanded] = useState({}); // { quoteId: true }
  const [selected, setSelected] = useState({}); // { quoteId: { lineId: true } }
  const [filterText, setFilterText] = useState('');
  const [filterByProject, setFilterByProject] = useState(Boolean(projectId));

  useEffect(() => {
    const fetchQuotes = async () => {
      setLoading(true);
      try {
        const params = {};
        if (filterByProject && projectId) params.project_id = projectId;
        params.latest_only = true;
        params.status = 'active';
        const { data } = await axiosInstance.get('/vendor-quotations', { params });
        setQuotations(data.quotations || []);
        // Auto-expand the first one if a project was selected to make
        // the import action feel one-step shorter.
        if ((data.quotations || []).length === 1) {
          setExpanded({ [data.quotations[0].id]: true });
        }
      } catch (e) {
        toast.error(t('vendor_quote_import.load_failed', 'Failed to load vendor quotations'));
        setQuotations([]);
      } finally {
        setLoading(false);
      }
    };
    fetchQuotes();
  }, [projectId, filterByProject, t]);

  const toggleExpand = (quoteId) => {
    setExpanded((p) => ({ ...p, [quoteId]: !p[quoteId] }));
  };

  const toggleLine = (quoteId, lineId) => {
    setSelected((p) => {
      const next = { ...p, [quoteId]: { ...(p[quoteId] || {}) } };
      if (next[quoteId][lineId]) {
        delete next[quoteId][lineId];
      } else {
        next[quoteId][lineId] = true;
      }
      return next;
    });
  };

  const toggleAllLines = (quote) => {
    setSelected((p) => {
      const current = p[quote.id] || {};
      const allSelected = (quote.lines || []).every((li) => current[li.id]);
      const next = { ...p };
      if (allSelected) {
        delete next[quote.id];
      } else {
        next[quote.id] = {};
        (quote.lines || []).forEach((li) => { next[quote.id][li.id] = true; });
      }
      return next;
    });
  };

  const filteredQuotations = quotations.filter((q) => {
    if (!filterText) return true;
    const needle = filterText.toLowerCase();
    return (
      (q.quote_no || '').toLowerCase().includes(needle) ||
      (q.supplier_name || '').toLowerCase().includes(needle) ||
      (q.lines || []).some((li) => (li.description || '').toLowerCase().includes(needle))
    );
  });

  const totalSelected = Object.values(selected).reduce(
    (sum, lines) => sum + Object.keys(lines).length, 0,
  );

  const handleImport = () => {
    if (totalSelected === 0) {
      toast.error(t('vendor_quote_import.no_lines_selected', 'Select at least one line to import'));
      return;
    }
    const items = [];
    quotations.forEach((q) => {
      const picks = selected[q.id] || {};
      (q.lines || []).forEach((li) => {
        if (!picks[li.id]) return;
        const qty = Number(li.quantity || 1);
        const unitPrice = Number(li.unit_price || 0);
        items.push({
          description: li.description || '',
          quantity: qty,
          unit: li.uom || 'unit',
          unit_price: unitPrice,
          total: qty * unitPrice,
          // Imported rows are NOT bound to a customer-side inventory item.
          // Leaving inventory_id blank flips Quotations.js into the editable
          // text-input branch so the user can refine the description.
          inventory_id: '',
          // Provenance — useful for audit but optional on the parent side
          source_vendor_quote_id: q.id,
          source_vendor_quote_no: q.quote_no,
          source_supplier_name: q.supplier_name,
          source_item_code: li.item_code || '',
        });
      });
    });
    onImport(items);
    toast.success(
      t('vendor_quote_import.imported_count', { count: items.length, defaultValue: `${items.length} line(s) imported` })
    );
    onClose();
  };

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      data-testid="vendor-quote-import-overlay"
      style={{ zIndex: 2000 }}
    >
      <div
        className="modal-content max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="vendor-quote-import-modal"
      >
        <div className="modal-header flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-indigo-600" />
            <div>
              <h3 className="modal-title">
                {t('vendor_quote_import.title', 'Import from Vendor Quotations')}
              </h3>
              {projectName && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {t('vendor_quote_import.project', 'Project')}: <strong>{projectName}</strong>
                </p>
              )}
            </div>
          </div>
          <button
            data-testid="close-vendor-import-modal"
            onClick={onClose}
            className="btn-icon"
            type="button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-4 py-3 border-b bg-gray-50 flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              data-testid="vendor-import-search"
              type="text"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder={t('vendor_quote_import.search_placeholder', 'Search supplier, quote no, or item')}
              className="form-input pl-8 w-full text-sm"
            />
          </div>
          {projectId && (
            <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
              <input
                data-testid="vendor-import-filter-project"
                type="checkbox"
                checked={filterByProject}
                onChange={(e) => setFilterByProject(e.target.checked)}
              />
              {t('vendor_quote_import.only_this_project', 'Only this project')}
            </label>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-12 text-gray-500" data-testid="vendor-import-loading">
              {t('common.loading', 'Loading...')}
            </div>
          ) : filteredQuotations.length === 0 ? (
            <div className="text-center py-12" data-testid="vendor-import-empty">
              <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-600 font-medium">
                {t('vendor_quote_import.empty_title', 'No vendor quotations found')}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {projectId && filterByProject
                  ? t('vendor_quote_import.empty_with_project', 'No vendor quotes are linked to this project yet. Try unchecking "Only this project".')
                  : t('vendor_quote_import.empty_no_project', 'Create or upload a vendor quotation first from Procurement.')}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredQuotations.map((q) => {
                const picks = selected[q.id] || {};
                const pickedCount = Object.keys(picks).length;
                const allChecked = (q.lines || []).length > 0
                  && (q.lines || []).every((li) => picks[li.id]);
                const isOpen = !!expanded[q.id];
                return (
                  <div
                    key={q.id}
                    className="border border-gray-200 rounded-lg overflow-hidden bg-white"
                    data-testid={`vendor-quote-row-${q.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleExpand(q.id)}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                        <div className="text-left min-w-0">
                          <div className="font-semibold text-gray-900 truncate">
                            {q.quote_no} · {q.supplier_name || t('common.unknown', 'Unknown supplier')}
                          </div>
                          <div className="text-xs text-gray-500">
                            {(q.lines || []).length} {t('vendor_quote_import.lines', 'lines')}
                            {q.quote_date ? ` · ${q.quote_date}` : ''}
                            {q.grand_total ? ` · ${Number(q.grand_total).toFixed(2)} ${q.currency || 'SAR'}` : ''}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {pickedCount > 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold">
                            <CheckCircle2 className="w-3 h-3" />
                            {pickedCount}
                          </span>
                        )}
                      </div>
                    </button>
                    {isOpen && (
                      <div className="border-t bg-gray-50">
                        <div className="px-4 py-2 flex items-center justify-between bg-gray-100">
                          <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer select-none">
                            <input
                              data-testid={`vendor-quote-select-all-${q.id}`}
                              type="checkbox"
                              checked={allChecked}
                              onChange={() => toggleAllLines(q)}
                            />
                            {allChecked
                              ? t('vendor_quote_import.deselect_all', 'Deselect all')
                              : t('vendor_quote_import.select_all', 'Select all lines')}
                          </label>
                          <span className="text-xs text-gray-500">
                            {pickedCount} / {(q.lines || []).length}
                          </span>
                        </div>
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100 text-gray-600">
                            <tr>
                              <th className="px-3 py-2 w-10"></th>
                              <th className="px-3 py-2 text-left">{t('vendor_quote_import.description', 'Description')}</th>
                              <th className="px-3 py-2 text-center w-20">{t('vendor_quote_import.qty', 'Qty')}</th>
                              <th className="px-3 py-2 text-center w-20">{t('vendor_quote_import.uom', 'UoM')}</th>
                              <th className="px-3 py-2 text-right w-28">{t('vendor_quote_import.unit_price', 'Unit Price')}</th>
                              <th className="px-3 py-2 text-right w-28">{t('vendor_quote_import.total', 'Total')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(q.lines || []).map((li) => {
                              const isChecked = !!picks[li.id];
                              return (
                                <tr
                                  key={li.id}
                                  className={`border-t cursor-pointer ${isChecked ? 'bg-indigo-50' : 'hover:bg-white'}`}
                                  onClick={() => toggleLine(q.id, li.id)}
                                  data-testid={`vendor-quote-line-${li.id}`}
                                >
                                  <td className="px-3 py-2 text-center">
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() => toggleLine(q.id, li.id)}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </td>
                                  <td className="px-3 py-2 text-gray-900">{li.description || '—'}</td>
                                  <td className="px-3 py-2 text-center" style={{ direction: 'ltr' }}>
                                    {Number(li.quantity || 0)}
                                  </td>
                                  <td className="px-3 py-2 text-center">{li.uom || 'ea'}</td>
                                  <td className="px-3 py-2 text-right" style={{ direction: 'ltr' }}>
                                    {Number(li.unit_price || 0).toFixed(2)}
                                  </td>
                                  <td className="px-3 py-2 text-right font-medium" style={{ direction: 'ltr' }}>
                                    {Number(li.total || (li.quantity || 0) * (li.unit_price || 0)).toFixed(2)}
                                  </td>
                                </tr>
                              );
                            })}
                            {(q.lines || []).length === 0 && (
                              <tr>
                                <td colSpan={6} className="px-3 py-4 text-center text-gray-500">
                                  {t('vendor_quote_import.no_lines', 'This quotation has no line items.')}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t bg-white flex items-center justify-between gap-3">
          <div className="text-sm text-gray-600">
            <span className="font-bold text-indigo-700" data-testid="vendor-import-selected-count">
              {totalSelected}
            </span>{' '}
            {t('vendor_quote_import.line_selected', 'line(s) selected')}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              data-testid="vendor-import-cancel"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="button"
              onClick={handleImport}
              disabled={totalSelected === 0}
              className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="vendor-import-confirm"
            >
              <CheckCircle2 className="w-4 h-4" />
              {t('vendor_quote_import.import_btn', 'Import Selected')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorQuoteImportModal;
